import React, { useState, useEffect, useRef, useCallback, Component } from "react";
import { Link, NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import videoBg from "../assets/video1.mp4";
import bgFallback from "../assets/fallback.png";
import hotst from "../assets/hotst.png";
import chemical from "../assets/chemicals.png";
import ink from "../assets/encreai.png";
import blanchet from "../assets/blanchet3.png";
import Navbar from "./Navbar.jsx";
import Footer from "./Footer.jsx";
import clientcare from "../assets/serviceclient.png";
import delivery from "../assets/transport.png";
import payement from "../assets/payement.png";

const CONFIG = {
  siteUrl: (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 
           process.env.REACT_APP_SITE_URL || 
           "https://graphicup.tn",
  enableVideo: (typeof import.meta !== 'undefined' && import.meta.env?.VITE_ENABLE_VIDEO !== "false") || 
               process.env.REACT_APP_ENABLE_VIDEO !== "false",
};

const FONTS = {
  clashDisplay: { fontFamily: "'Clash Display', sans-serif" },
  manrope: { fontFamily: "'Manrope', sans-serif" }
};

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught:", error, errorInfo);
    if (typeof window !== 'undefined' && window.Sentry) {
      window.Sentry.captureException(error, { extra: errorInfo });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white p-4">
          <div className="text-center max-w-md">
            <h1 className="text-3xl md:text-4xl font-bold mb-4" style={FONTS.clashDisplay}>
              Oops! Quelque chose s'est mal passé
            </h1>
            <p className="text-lg mb-6" style={FONTS.manrope}>
              Veuillez rafraîchir la page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition-colors"
              style={FONTS.clashDisplay}
            >
              Rafraîchir
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const ProgressiveImage = ({ src, alt, className, fallback = bgFallback, priority = false }) => {
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    if (!imgRef.current) return;
    
    if (imgRef.current.complete) {
      setLoaded(true);
    }
  }, []);

  return (
    <div className="relative w-full h-full">
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
          <div className="w-12 h-12 border-3 border-[#158DC1] border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      <img
        ref={imgRef}
        src={error ? fallback : src}
        alt={alt}
        className={`${className} ${loaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-500`}
        onError={() => setError(true)}
        onLoad={() => setLoaded(true)}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
      />
    </div>
  );
};

const HoverButton = ({ children, to, onClick, className = "" }) => {
  const buttonClass = `px-6 py-3 bg-[#158DC1] text-white rounded-[15px] hover:bg-white hover:text-[#158DC1] transition-all duration-300 border-2 border-transparent hover:border-[#158DC1] shadow-lg hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-[#158DC1] focus:ring-opacity-50 min-h-[44px] flex items-center justify-center ${className}`;

  if (to) {
    return (
      <Link to={to} className="inline-block">
        <button className={buttonClass} style={FONTS.clashDisplay}>
          {children}
        </button>
      </Link>
    );
  }

  return (
    <button onClick={onClick} className={buttonClass} style={FONTS.clashDisplay}>
      {children}
    </button>
  );
};

const OptimizedHeroVideo = ({ onLoaded }) => {
  const [videoError, setVideoError] = useState(false);
  const [showPoster, setShowPoster] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    if (typeof navigator !== 'undefined' && 'connection' in navigator) {
      const conn = navigator.connection;
      if (conn.saveData || conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g') {
        setVideoError(true);
        onLoaded();
        return;
      }
    }

    const video = videoRef.current;
    if (video) {
      const handlePlay = () => setShowPoster(false);
      video.addEventListener('playing', handlePlay);
      return () => video.removeEventListener('playing', handlePlay);
    }
  }, [onLoaded]);

  const handleVideoError = useCallback(() => {
    console.warn("Video failed to load, showing fallback image");
    setVideoError(true);
    onLoaded();
  }, [onLoaded]);

  const handleVideoLoad = useCallback(() => {
    onLoaded();
  }, [onLoaded]);

  if (videoError) {
    return (
      <img
        src={bgFallback}
        alt="Graphic Up - Impression professionnelle"
        className="absolute w-full h-full object-cover"
        loading="eager"
      />
    );
  }

  return (
    <>
      {showPoster && (
        <img
          src={bgFallback}
          alt="Graphic Up"
          className="absolute w-full h-full object-cover transition-opacity duration-500"
          style={{ opacity: showPoster ? 1 : 0 }}
        />
      )}
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
        poster={bgFallback}
        className="absolute w-full h-full object-cover"
        src={videoBg}
        onLoadedData={handleVideoLoad}
        onCanPlayThrough={handleVideoLoad}
        onError={handleVideoError}
      >
        <source src={videoBg} type="video/mp4" />
      </video>
    </>
  );
};

const ProductSection = ({ 
  title, 
  description, 
  imageSrc, 
  imageAlt, 
  variant = "default",
  index 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const variants = {
    default: {
      container: "flex-col lg:flex-row",
      content: "lg:w-3/5",
      image: "lg:w-3/5",
      contentOrder: "order-2 lg:order-1",
      imageOrder: "order-1 lg:order-2",
      gradient: "from-white to-gray-50",
      accentPosition: "top-0 right-0"
    },
    reversed: {
      container: "flex-col lg:flex-row",
      content: "lg:w-3/5",
      image: "lg:w-3/5",
      contentOrder: "order-2 lg:order-2",
      imageOrder: "order-1 lg:order-1",
      gradient: "from-gray-50 to-white",
      accentPosition: "top-0 left-0"
    }
  };

  const config = variants[variant] || variants.default;

  return (
    <div
      ref={sectionRef}
      className={`relative flex ${config.container} gap-x-15 lg:min-h-screen rounded-[60px] z-10 overflow-hidden bg-gradient-to-br ${config.gradient} max-w-8xl mx-auto px-6 py-8 sm:px-8 sm:py-10 lg:px-12 lg:py-8 transition-all duration-500 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className={`absolute ${config.accentPosition} w-64 h-64 bg-[#158DC1]/5 rounded-full blur-3xl -z-10`}></div>
      
      <div className={`${config.contentOrder} relative flex flex-col justify-center ${config.content} px-4 py-6 lg:px-12 lg:py-5 text-left text-black`}>
        {/* Decorative line */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-0.5 bg-gradient-to-r from-[#158DC1] to-transparent"></div>
          <span className="text-[#158DC1] text-sm font-semibold tracking-wider" style={FONTS.clashDisplay}>
            Offset
          </span>
          <div className="w-12 h-0.5 bg-gradient-to-r from-transparent to-[#158DC1]"></div>
        </div>
        
        <h2
          className="text-xl sm:text-2xl md:text-3xl lg:text-h2 font-bold mb-4 text-gray-900"
          style={FONTS.clashDisplay}
        >
          {title}
        </h2>
        <p 
          className="text-base sm:text-lg md:text-lg lg:text-xl mb-4 sm:mb-6 md:mb-8 lg:mb-10 max-w-[850px] leading-relaxed text-gray-700"
          style={FONTS.manrope}
        >
          {description}
        </p>
        {/* button with icon like CTA */}
        <HoverButton to="/produits" className="px-8 py-3.5 text-lg min-w-[180px]">
          <span className="flex items-center justify-center gap-2">
            Explorer
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </span>
        </HoverButton>
      </div>
      
      <div className={`${config.imageOrder} w-full ${config.image} flex items-center justify-center lg:px-6 lg:py-36 relative z-10`}>
        <ProgressiveImage 
          src={imageSrc} 
          alt={imageAlt} 
          className="w-full max-w-[600px] h-auto"
          priority={index === 0}
        />
      </div>
    </div>
  );
};





export default function Landing() {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    
    const preloadImages = [
      { src: ink, priority: true },
      { src: chemical, priority: true },
      { src: hotst, priority: false },
      { src: blanchet, priority: false }
    ];

    preloadImages.forEach(({ src, priority }) => {
      const link = document.createElement('link');
      link.rel = priority ? 'preload' : 'prefetch';
      link.as = 'image';
      link.href = src;
      document.head.appendChild(link);
    });

    const timer = setTimeout(() => setVideoLoaded(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const productSections = [
    {
      title: "Matière Première",
      description: "Nous proposons une large gamme de matières premières de haute qualité pour l'impression offset, garantissant des résultats exceptionnels et une production fluide. Notre sélection comprend des encres d'impression premium, des plaques offset durables, ainsi que des papiers spécialisés.",
      imageSrc: ink,
      imageAlt: "Encre d'impression haute qualité",
      variant: "default"
    },
    {
      title: "Additifs chimiques",
      description: "Nos additifs chimiques jouent un rôle essentiel dans l'optimisation du processus d'impression offset. Graphic Up vous offre une gamme complète d'additifs de haute performance conçus pour améliorer la stabilité de l'encrage et prolonger la durée de vie des équipements.",
      imageSrc: chemical,
      imageAlt: "Additifs chimiques pour impression",
      variant: "reversed"
    },
    {
      title: "Fourniture de Finition",
      description: "Nos fournitures de finition permettent d'apporter une valeur esthétique et distinctive à vos impressions. Nous proposons une sélection de produits destinés à sublimer vos créations avec des effets spéciaux et une présentation élégante.",
      imageSrc: hotst,
      imageAlt: "Fournitures de finition pour impression",
      variant: "default"
    },
    {
      title: "Consommables",
      description: "Nos consommables d'impression sont essentiels pour garantir la performance et la fiabilité de vos presses offset. Nous fournissons une large gamme de produits sélectionnés pour leur qualité et leur durabilité.",
      imageSrc: blanchet,
      imageAlt: "Consommables d'impression",
      variant: "reversed"
    }
  ];

  return (
    <ErrorBoundary>
      <div className="w-full bg-black relative overflow-x-hidden">
        <a 
          href="#main-content" 
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-white p-2 z-50 rounded-lg font-bold"
        >
          Aller au contenu principal
        </a>

        <Navbar />
        
        <Helmet>
          <html lang="fr" />
          <title>Graphic Up – Fournisseur de produits pour l'impression et arts graphiques</title>
          <meta name="description" content="Graphic Up accompagne les imprimeurs et professionnels des arts graphiques avec des produits fiables et innovants. Matières premières, additifs chimiques, finitions et consommables." />
          <link rel="canonical" href={`${CONFIG.siteUrl}/`} />
          <meta property="og:title" content="Graphic Up – Fournisseur de produits pour l'impression et arts graphiques" />
          <meta property="og:description" content="Graphic Up accompagne les imprimeurs et professionnels des arts graphiques avec des produits fiables et innovants." />
          <meta property="og:image" content={`${CONFIG.siteUrl}/logoGU.png`} />
          <meta property="og:url" content={`${CONFIG.siteUrl}/`} />
          <meta property="og:type" content="website" />
          <meta property="og:locale" content="fr_FR" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Graphic Up – Fournisseur de produits pour l'impression" />
          <meta name="twitter:description" content="Matières premières, additifs chimiques, finitions et consommables pour l'impression offset." />
          <meta name="robots" content="index, follow, max-image-preview:large" />
          <meta name="googlebot" content="index, follow" />
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "Graphic Up",
              "description": "Fournisseur de produits pour l'impression et arts graphiques en Tunisie",
              "url": CONFIG.siteUrl,
              "logo": `${CONFIG.siteUrl}/logoGU.png`,
              "contactPoint": {
                "@type": "ContactPoint",
                "contactType": "Sales"
              },
              "areaServed": "TN",
              "serviceType": ["Matières premières", "Additifs chimiques", "Fourniture de finition", "Consommables"]
            })}
          </script>
        </Helmet>

        <section 
          id="main-content" 
          className="relative h-screen w-full flex items-center justify-center text-center text-white overflow-hidden"
          aria-label="Section héros"
        >
          <OptimizedHeroVideo onLoaded={() => setVideoLoaded(true)} />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/5 to-black/40 z-0"></div>

          <div className={`relative z-10 mt-10 md:mt-24 max-w-8xl px-4 transition-all duration-1000 ${
            mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}>
            <p className="text-xl sm:text-2xl md:text-h3 font-bold text-white mb-2" style={FONTS.clashDisplay}>
              Graphic Up
            </p>

            <h1 className="text-3xl sm:text-4xl md:text-4xl lg:text-h1 font-bold text-white m-4 leading-tight" style={{...FONTS.clashDisplay, textShadow: '0 2px 20px rgba(0,0,0,0.5)'}}>
              L'âme de vos impressions
            </h1>

            <p className="text-base sm:text-lg md:text-xl lg:text-2xl italic text-white max-w-3xl mx-auto leading-relaxed" style={{...FONTS.manrope, textShadow: '0 2px 15px rgba(0,0,0,0.5)'}}>
              Votre partenaire de confiance pour sublimer chaque impression
            </p>

            <div className="mt-10 sm:mt-14">
              <NavLink to="/produits">
                <button 
                  className="text-white border-[3px] border-white text-lg sm:text-xl font-bold bg-transparent hover:bg-white hover:text-black px-8 sm:px-10 py-3 sm:py-4 rounded-[15px] transition-all duration-300 shadow-lg hover:shadow-2xl focus:outline-none focus:ring-4 focus:ring-white focus:ring-opacity-50 min-h-[44px] backdrop-blur-sm hover:backdrop-blur-none"
                  style={FONTS.clashDisplay}
                  aria-label="Découvrir nos produits"
                >
                  Nos Produits
                </button>
              </NavLink>
            </div>
          </div>

          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce" aria-hidden="true">
            <div className="w-6 h-10 border-2 border-white/80 rounded-full flex justify-center backdrop-blur-sm">
              <div className="w-1 h-3 bg-white rounded-full mt-2 animate-pulse"></div>
            </div>
          </div>
        </section>

        <div className="relative z-0">
          {productSections.map((section, index) => (
            <React.Fragment key={index}>
              <div className={index === 0 ? "" : "mt-28"}>
                <ProductSection {...section} index={index} />
              </div>
              
              {index === 1 && (
                <section className="relative py-20 px-6 mt-28 bg-black">
                  <div className="max-w-7xl mx-auto text-center">
                    {/* Decorative line */}
                    <div className="flex items-center justify-center gap-4 mb-10">
                      <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-[#158DC1] to-transparent"></div>
                      <span className="text-white/70 text-sm font-semibold tracking-wider" style={FONTS.clashDisplay}>
                        POURQUOI NOUS CHOISIR ?
                      </span>
                      <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-[#158DC1] to-transparent"></div>
                    </div>
                    
                    {/* Main heading */}
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-10" style={FONTS.clashDisplay}>
                      Pourquoi{" "}
                      <span className="text-[#158DC1]">
                        Graphic Up
                      </span>
                      {" "}
                    </h2>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-10">
                      <div className="bg-white rounded-3xl p-8 shadow-2xl transition-all duration-300">
                        <img
                          src={delivery}
                          alt="Livraison rapide"
                          className="w-20 h-20 mx-auto mb-6 object-contain"
                        />
                        <h3 className="text-xl font-bold text-black mb-3" style={FONTS.clashDisplay}>
                          Livraison <span className="text-[#158DC1]">Rapide</span>
                        </h3>
                        <p className="text-gray-700 text-base leading-relaxed" style={FONTS.manrope}>
                          Nous assurons une livraison rapide et fiable pour garantir le bon déroulement de votre production.
                        </p>
                      </div>

                      <div className="bg-white rounded-3xl p-8 shadow-2xl transition-all duration-300">
                        <img
                          src={payement}
                          alt="Paiement flexible"
                          className="w-20 h-20 mx-auto mb-6 object-contain"
                        />
                        <h3 className="text-xl font-bold text-black mb-3" style={FONTS.clashDisplay}>
                          Paiement <span className="text-[#158DC1]">Flexible</span>
                        </h3>
                        <p className="text-gray-700 text-base leading-relaxed" style={FONTS.manrope}>
                          Nous proposons des solutions de paiement adaptées à vos besoins pour faciliter vos achats.
                        </p>
                      </div>

                      <div className="bg-white rounded-3xl p-8 shadow-2xl transition-all duration-300">
                        <img
                          src={clientcare}
                          alt="Service client"
                          className="w-20 h-20 mx-auto mb-6 object-contain"
                        />
                        <h3 className="text-xl font-bold text-black mb-3" style={FONTS.clashDisplay}>
                          Service <span className="text-[#158DC1]">Client</span>
                        </h3>
                        <p className="text-gray-700 text-base leading-relaxed" style={FONTS.manrope}>
                          Un service à l'écoute, réactif et professionnel pour vous accompagner dans chaque projet.
                        </p>
                      </div>
                    </div>
                  </div>
                </section>
              )}
            </React.Fragment>
          ))}


          {/* Simple CTA Section */}
          <section className="relative py-20 px-6 mt-28 bg-black">
            <div className="max-w-4xl mx-auto text-center">
              {/* Decorative line */}
              <div className="flex items-center justify-center gap-4 mb-10">
                <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-[#158DC1] to-transparent"></div>
                <span className="text-white/70 text-sm font-semibold tracking-wider" style={FONTS.clashDisplay}>
                  PRÊT À TRANSFORMER VOS IMPRESSIONS ?
                </span>
                <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-[#158DC1] to-transparent"></div>
              </div>
              
              {/* Main heading */}
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6" style={FONTS.clashDisplay}>
                Donnez vie à vos <span className="text-[#158DC1]">projets</span> d'impression
              </h2>
              
              {/* Description */}
              <p className="text-lg text-white/80 max-w-2xl mx-auto mb-10 leading-relaxed" style={FONTS.manrope}>
                Contactez-nous dès aujourd'hui pour discuter de vos besoins et découvrir comment Graphic Up peut optimiser votre production.
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                <HoverButton 
                  to="/contact"
                  className="px-10 py-3.5 text-lg min-w-[220px]"
                >
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    Demander un devis
                  </span>
                </HoverButton>
                
                <Link to="/produits" aria-label="Voir nos produits">
                  <button 
                    className="px-10 py-3.5 text-lg min-w-[220px] border-2 border-white/30 text-white hover:bg-white hover:text-black hover:border-white rounded-[15px] transition-all duration-300"
                    style={FONTS.clashDisplay}
                  >
                    <span className="flex items-center justify-center gap-2">
                      Voir nos produits
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                  </button>
                </Link>
              </div>
            </div>
          </section>

          <div className="w-full h-25 -z-40 bg-black mt-28"></div>
          <Footer />
        </div>

        <style>{`
          @keyframes blob {
            0%, 100% {
              transform: translate(0, 0) scale(1);
            }
            25% {
              transform: translate(20px, -50px) scale(1.1);
            }
            50% {
              transform: translate(-20px, 20px) scale(0.9);
            }
            75% {
              transform: translate(50px, 50px) scale(1.05);
            }
          }

          @keyframes float {
            0%, 100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-20px);
            }
          }

          .animate-blob {
            animation: blob 20s infinite;
          }

          .animate-float {
            animation: float 6s ease-in-out infinite;
          }

          html {
            scroll-behavior: smooth;
          }

          ::-webkit-scrollbar {
            width: 8px;
          }

          ::-webkit-scrollbar-track {
            background: #000;
          }

          ::-webkit-scrollbar-thumb {
            background: linear-gradient(180deg, #158DC1, #5fb030);
            border-radius: 4px;
          }

          ::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(180deg, #0f6e9c, #4a9028);
          }

          button:focus-visible,
          a:focus-visible {
            outline: 2px solid #158DC1;
            outline-offset: 2px;
          }

          @media (prefers-reduced-motion: reduce) {
            * {
              animation-duration: 0.01ms !important;
              animation-iteration-count: 1 !important;
              transition-duration: 0.01ms !important;
            }
          }

          body {
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
          }
        `}</style>
      </div>
    </ErrorBoundary>
  );
}